import { useCallback, useEffect, useState, type FormEvent } from "react";
import { api } from "../api";
import type { PanelUser } from "../types";

type ChecklistItem = {
  id: string;
  title: string;
  status: string;
  detail: string;
};

type FirewallRule = {
  id: string;
  action: string;
  port: string;
  protocol: string;
  source: string | null;
  comment: string | null;
  enabled: boolean;
};

type AllowEntry = {
  id: string;
  cidr: string;
  label: string;
};

export function SecurityPage({
  user,
  onChangePassword,
  onSetup2fa,
  onEnable2fa,
  onDisable2fa,
  onRefresh
}: {
  user: PanelUser;
  onChangePassword: (current: string, next: string) => Promise<void>;
  onSetup2fa: () => Promise<{ secret: string; otpauthUrl: string }>;
  onEnable2fa: (code: string) => Promise<void>;
  onDisable2fa: (password: string, code: string) => Promise<void>;
  onRefresh: () => Promise<void>;
}) {
  const staff = user.role === "superadmin" || user.role === "admin";
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [secret, setSecret] = useState("");
  const [otpauth, setOtpauth] = useState("");
  const [code, setCode] = useState("");
  const [disablePass, setDisablePass] = useState("");
  const [disableCode, setDisableCode] = useState("");

  // Hardening state (staff)
  const [status, setStatus] = useState<Record<string, unknown> | null>(null);
  const [rules, setRules] = useState<FirewallRule[]>([]);
  const [jails, setJails] = useState<
    { name: string; bannedCount: number; bannedIps: string[] }[]
  >([]);
  const [allowlist, setAllowlist] = useState<AllowEntry[]>([]);
  const [updates, setUpdates] = useState<{
    packages?: string[];
    note?: string;
    manager?: string;
  } | null>(null);
  const [newPort, setNewPort] = useState("8080");
  const [newCidr, setNewCidr] = useState("");
  const [newCidrLabel, setNewCidrLabel] = useState("");
  const [banIp, setBanIp] = useState("");
  const [hotDomain, setHotDomain] = useState("");

  const loadHardening = useCallback(async () => {
    if (!staff) return;
    try {
      const s = await api<{ status: Record<string, unknown> }>(
        "/api/v1/security/status"
      );
      setStatus(s.status || null);
      const fw = await api<{ items: FirewallRule[] }>(
        "/api/v1/security/firewall"
      );
      setRules(fw.items || []);
      const f2b = await api<{
        jails: { name: string; bannedCount: number; bannedIps: string[] }[];
      }>("/api/v1/security/fail2ban");
      setJails(f2b.jails || []);
      const al = await api<{ items: AllowEntry[] }>(
        "/api/v1/security/allowlist"
      );
      setAllowlist(al.items || []);
      const u = await api<{ updates: Record<string, unknown> }>(
        "/api/v1/security/updates"
      );
      setUpdates((u.updates as typeof updates) || null);
    } catch (error) {
      setErr(error instanceof Error ? error.message : String(error));
    }
  }, [staff]);

  useEffect(() => {
    void loadHardening();
  }, [loadHardening]);

  const changePw = async (e: FormEvent) => {
    e.preventDefault();
    setErr("");
    setMsg("");
    try {
      await onChangePassword(cur, next);
      setMsg("Password updated.");
      setCur("");
      setNext("");
    } catch (error) {
      setErr(error instanceof Error ? error.message : String(error));
    }
  };

  const setup = async () => {
    setErr("");
    try {
      const res = await onSetup2fa();
      setSecret(res.secret);
      setOtpauth(res.otpauthUrl);
    } catch (error) {
      setErr(error instanceof Error ? error.message : String(error));
    }
  };

  const enable = async (e: FormEvent) => {
    e.preventDefault();
    setErr("");
    try {
      await onEnable2fa(code);
      setMsg("Two-factor authentication enabled.");
      setSecret("");
      setCode("");
      await onRefresh();
    } catch (error) {
      setErr(error instanceof Error ? error.message : String(error));
    }
  };

  const disable = async (e: FormEvent) => {
    e.preventDefault();
    setErr("");
    try {
      await onDisable2fa(disablePass, disableCode);
      setMsg("Two-factor authentication disabled.");
      setDisablePass("");
      setDisableCode("");
      await onRefresh();
    } catch (error) {
      setErr(error instanceof Error ? error.message : String(error));
    }
  };

  const checklist = ((status?.checklist as ChecklistItem[]) || []) as ChecklistItem[];
  const okCount = checklist.filter((c) => c.status === "ok").length;
  const failCount = checklist.filter((c) => c.status === "fail").length;
  const warnCount = checklist.filter(
    (c) => c.status !== "ok" && c.status !== "fail"
  ).length;

  return (
    <>
      <div className="topbar">
        <div>
          <h1>{staff ? "Security Advisor" : "Password & Security"}</h1>
          <p>
            {staff
              ? "WHM · hardening score, firewall, Fail2ban, IP allowlist, and your login security"
              : "Change password and enable two-factor authentication"}
          </p>
        </div>
        <div className="topbar-actions">
          {staff ? (
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => void loadHardening()}
            >
              Refresh advisor
            </button>
          ) : null}
          <div className="phase-pill">
            2FA {user.totpEnabled ? "on" : "off"}
            {status ? ` · score ${String(status.score ?? "—")}` : ""}
          </div>
        </div>
      </div>

      {msg ? <div className="success-banner">{msg}</div> : null}
      {err ? <div className="error-banner">{err}</div> : null}

      {staff && status ? (
        <div className="whm-stats" style={{ marginBottom: 16 }}>
          <div className="whm-stat">
            <span className="whm-stat-label">Hardening score</span>
            <span className="whm-stat-value">
              {String(status.score ?? "—")}
              <span className="faint" style={{ fontSize: 16 }}>
                /100
              </span>
            </span>
            <span className="whm-stat-meta">
              {okCount} ok · {failCount} fail · {warnCount} other
            </span>
          </div>
          <div className="whm-stat">
            <span className="whm-stat-label">Firewall</span>
            <span className="whm-stat-value" style={{ fontSize: 16 }}>
              {String(status.firewallBackend || "—")}
            </span>
            <span className="whm-stat-meta">{rules.length} rule(s)</span>
          </div>
          <div className="whm-stat">
            <span className="whm-stat-label">Fail2ban</span>
            <span className="whm-stat-value">
              {jails.reduce((n, j) => n + (j.bannedCount || 0), 0)}
            </span>
            <span className="whm-stat-meta">
              {jails.length} jail(s) · banned IPs
            </span>
          </div>
          <div className="whm-stat">
            <span className="whm-stat-label">Allowlist</span>
            <span className="whm-stat-value">{allowlist.length}</span>
            <span className="whm-stat-meta">Trusted CIDRs for admin</span>
          </div>
        </div>
      ) : null}

      <div className="grid">
        <section className="card wide">
          <div className="card-label">Change password</div>
          <form onSubmit={changePw} className="stack-form">
            <div className="field">
              <label>Current password</label>
              <input
                type="password"
                value={cur}
                onChange={(e) => setCur(e.target.value)}
                required
              />
            </div>
            <div className="field">
              <label>New password</label>
              <input
                type="password"
                value={next}
                onChange={(e) => setNext(e.target.value)}
                required
                minLength={8}
              />
            </div>
            <button className="btn btn-primary btn-inline" type="submit">
              Update password
            </button>
          </form>
        </section>

        <section className="card wide">
          <div className="card-label">Two-factor authentication (TOTP)</div>
          {!user.totpEnabled ? (
            <>
              <p className="card-meta" style={{ marginBottom: 14 }}>
                Protect staff and end-user logins with an authenticator app.
              </p>
              {!secret ? (
                <button
                  type="button"
                  className="btn btn-primary btn-inline"
                  onClick={() => void setup()}
                >
                  Set up 2FA
                </button>
              ) : (
                <form onSubmit={enable} className="stack-form">
                  <p className="card-meta">
                    Add this secret to your authenticator, then enter a code.
                  </p>
                  <div className="secret-box mono">{secret}</div>
                  <p
                    className="card-meta mono"
                    style={{ wordBreak: "break-all" }}
                  >
                    {otpauth}
                  </p>
                  <div className="field">
                    <label>Verification code</label>
                    <input
                      value={code}
                      onChange={(e) => setCode(e.target.value)}
                      inputMode="numeric"
                      required
                      placeholder="000000"
                    />
                  </div>
                  <button className="btn btn-primary btn-inline" type="submit">
                    Enable 2FA
                  </button>
                </form>
              )}
            </>
          ) : (
            <form onSubmit={disable} className="stack-form">
              <p className="card-meta" style={{ marginBottom: 10 }}>
                2FA is active on this account.
              </p>
              <div className="field">
                <label>Password</label>
                <input
                  type="password"
                  value={disablePass}
                  onChange={(e) => setDisablePass(e.target.value)}
                  required
                />
              </div>
              <div className="field">
                <label>Authenticator code</label>
                <input
                  value={disableCode}
                  onChange={(e) => setDisableCode(e.target.value)}
                  required
                />
              </div>
              <button className="btn btn-ghost" type="submit">
                Disable 2FA
              </button>
            </form>
          )}
        </section>
      </div>

      {staff && status ? (
        <>
          <div className="card full" style={{ marginTop: 20 }}>
            <div className="card-label">Security Advisor checklist</div>
            <p className="card-meta faint">
              {String(status.note || "Review items below and apply defaults.")}
            </p>
            <div className="table-wrap" style={{ marginTop: 12 }}>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Check</th>
                    <th>Status</th>
                    <th>Detail</th>
                  </tr>
                </thead>
                <tbody>
                  {checklist.map((c) => (
                    <tr key={c.id}>
                      <td>
                        <strong>{c.title}</strong>
                      </td>
                      <td>
                        <span
                          className={`badge ${
                            c.status === "ok"
                              ? "ok"
                              : c.status === "fail"
                                ? "bad"
                                : "warn"
                          }`}
                        >
                          {c.status === "ok"
                            ? "OK"
                            : c.status === "fail"
                              ? "Needs attention"
                              : c.status}
                        </span>
                      </td>
                      <td className="faint">{c.detail}</td>
                    </tr>
                  ))}
                  {checklist.length === 0 ? (
                    <tr>
                      <td colSpan={3} className="muted">
                        No checklist data — refresh advisor status.
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
            <div className="topbar-actions" style={{ marginTop: 12 }}>
              <button
                type="button"
                className="btn btn-primary btn-inline"
                onClick={async () => {
                  setErr("");
                  try {
                    await api("/api/v1/security/hardening", {
                      method: "POST",
                      body: "{}"
                    });
                    setMsg("Hardening defaults applied");
                    await loadHardening();
                  } catch (error) {
                    setErr(
                      error instanceof Error ? error.message : String(error)
                    );
                  }
                }}
              >
                Apply hardening defaults
              </button>
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => void loadHardening()}
              >
                Refresh status
              </button>
            </div>
          </div>

          <div className="grid" style={{ marginTop: 16 }}>
            <section className="card wide">
              <div className="card-label">
                Firewall · {String(status.firewallBackend || "shadow")}
              </div>
              <div className="topbar-actions" style={{ marginBottom: 10 }}>
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  onClick={async () => {
                    await api("/api/v1/security/firewall/defaults", {
                      method: "POST",
                      body: "{}"
                    });
                    setMsg("Default ports applied (22/80/443/7480)");
                    await loadHardening();
                  }}
                >
                  Apply default ports
                </button>
              </div>
              <form
                className="topbar-actions"
                style={{ marginBottom: 10 }}
                onSubmit={async (e) => {
                  e.preventDefault();
                  await api("/api/v1/security/firewall", {
                    method: "POST",
                    body: JSON.stringify({
                      action: "allow",
                      port: newPort,
                      protocol: "tcp"
                    })
                  });
                  setNewPort("");
                  await loadHardening();
                }}
              >
                <input
                  className="inline-select mono"
                  style={{ width: 100 }}
                  value={newPort}
                  onChange={(e) => setNewPort(e.target.value)}
                  placeholder="port"
                />
                <button className="btn btn-primary btn-sm" type="submit">
                  Allow port
                </button>
              </form>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Action</th>
                    <th>Port</th>
                    <th>Comment</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {rules.map((r) => (
                    <tr key={r.id}>
                      <td className="mono">{r.action}</td>
                      <td className="mono">
                        {r.port}/{r.protocol}
                      </td>
                      <td className="faint">{r.comment || "—"}</td>
                      <td>
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm"
                          onClick={async () => {
                            await api(`/api/v1/security/firewall/${r.id}`, {
                              method: "DELETE"
                            });
                            await loadHardening();
                          }}
                        >
                          Del
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>

            <section className="card wide">
              <div className="card-label">Fail2ban / bans</div>
              {jails.map((j) => (
                <div key={j.name} style={{ marginBottom: 10 }}>
                  <div className="mono">
                    {j.name} · {j.bannedCount} banned
                  </div>
                  {j.bannedIps?.length ? (
                    <div className="faint mono" style={{ fontSize: 12 }}>
                      {j.bannedIps.join(", ")}
                      {j.bannedIps.map((ip) => (
                        <button
                          key={ip}
                          type="button"
                          className="btn btn-ghost btn-sm"
                          style={{ marginLeft: 6 }}
                          onClick={async () => {
                            await api("/api/v1/security/fail2ban/unban", {
                              method: "POST",
                              body: JSON.stringify({ ip, jail: j.name })
                            });
                            setMsg(`Unbanned ${ip}`);
                            await loadHardening();
                          }}
                        >
                          unban {ip}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="faint">No banned IPs</div>
                  )}
                </div>
              ))}
              <form
                className="topbar-actions"
                onSubmit={async (e) => {
                  e.preventDefault();
                  await api("/api/v1/security/fail2ban/ban", {
                    method: "POST",
                    body: JSON.stringify({
                      ip: banIp,
                      jail: "realpanel-auth"
                    })
                  });
                  setBanIp("");
                  await loadHardening();
                }}
              >
                <input
                  className="inline-select mono"
                  value={banIp}
                  onChange={(e) => setBanIp(e.target.value)}
                  placeholder="1.2.3.4"
                />
                <button className="btn btn-ghost btn-sm" type="submit">
                  Manual ban
                </button>
              </form>
            </section>
          </div>

          <div className="grid" style={{ marginTop: 16 }}>
            <section className="card wide">
              <div className="card-label">Admin IP allowlist</div>
              <p className="card-meta faint">
                Empty = all staff IPs allowed. When non-empty, only listed
                CIDRs/IPs can use staff sessions.
              </p>
              <form
                className="topbar-actions"
                style={{ marginBottom: 10 }}
                onSubmit={async (e) => {
                  e.preventDefault();
                  await api("/api/v1/security/allowlist", {
                    method: "POST",
                    body: JSON.stringify({
                      cidr: newCidr,
                      label: newCidrLabel
                    })
                  });
                  setNewCidr("");
                  setNewCidrLabel("");
                  setMsg("Allowlist entry added");
                  await loadHardening();
                }}
              >
                <input
                  className="inline-select mono"
                  value={newCidr}
                  onChange={(e) => setNewCidr(e.target.value)}
                  placeholder="203.0.113.0/24"
                  required
                />
                <input
                  className="inline-select"
                  value={newCidrLabel}
                  onChange={(e) => setNewCidrLabel(e.target.value)}
                  placeholder="label"
                />
                <button className="btn btn-primary btn-sm" type="submit">
                  Add
                </button>
              </form>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>CIDR / IP</th>
                    <th>Label</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {allowlist.length === 0 ? (
                    <tr>
                      <td colSpan={3} className="faint">
                        No restrictions
                      </td>
                    </tr>
                  ) : (
                    allowlist.map((a) => (
                      <tr key={a.id}>
                        <td className="mono">{a.cidr}</td>
                        <td className="faint">{a.label || "—"}</td>
                        <td>
                          <button
                            type="button"
                            className="btn btn-ghost btn-sm"
                            onClick={async () => {
                              await api(
                                `/api/v1/security/allowlist/${a.id}`,
                                { method: "DELETE" }
                              );
                              await loadHardening();
                            }}
                          >
                            Del
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </section>

            <section className="card wide">
              <div className="card-label">Hotlink · updates · reboot</div>
              <form
                className="topbar-actions"
                style={{ marginBottom: 12 }}
                onSubmit={async (e) => {
                  e.preventDefault();
                  await api("/api/v1/security/hotlink", {
                    method: "POST",
                    body: JSON.stringify({
                      domain: hotDomain,
                      enabled: true
                    })
                  });
                  setMsg(`Hotlink protection enabled for ${hotDomain}`);
                  setHotDomain("");
                }}
              >
                <input
                  className="inline-select"
                  value={hotDomain}
                  onChange={(e) => setHotDomain(e.target.value)}
                  placeholder="domain.com"
                />
                <button className="btn btn-ghost btn-sm" type="submit">
                  Enable hotlink
                </button>
              </form>
              <p className="card-meta">
                OS updates ({updates?.manager || "—"}):{" "}
                {updates?.packages?.length
                  ? `${updates.packages.length} available`
                  : "none listed"}
              </p>
              <p className="card-meta faint">{updates?.note || ""}</p>
              {updates?.packages?.slice(0, 8).map((p) => (
                <div key={p} className="mono faint" style={{ fontSize: 12 }}>
                  {p}
                </div>
              ))}
              <p className="card-meta" style={{ marginTop: 12 }}>
                Panel TLS: terminate HTTPS at reverse proxy. Agent port 7420 must
                not be public.
              </p>
              <button
                type="button"
                className="btn btn-ghost"
                style={{ marginTop: 8 }}
                onClick={async () => {
                  if (
                    !confirm(
                      "Mark reboot pending? Real reboot only if REALPANEL_ALLOW_REBOOT=true on Linux."
                    )
                  )
                    return;
                  const res = await api<{ note?: string }>(
                    "/api/v1/security/reboot",
                    {
                      method: "POST",
                      body: JSON.stringify({ confirm: true })
                    }
                  );
                  setMsg(res.note || "Reboot requested");
                  await loadHardening();
                }}
              >
                Request reboot
              </button>
            </section>
          </div>

          <div className="card full" style={{ marginTop: 16 }}>
            <div className="card-label">Login protection</div>
            <p className="card-meta">
              Rate limit: 8 failed attempts / 15 min → 30 min block + shadow ban
              (realpanel-auth jail). Admin allowlist blocks staff API from
              non-listed IPs when configured.
            </p>
          </div>
        </>
      ) : null}
    </>
  );
}
